import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalOverlay,
  Text,
  VStack,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import { useDispatch, useSelector } from "react-redux";
import { hitBeApi } from "../../api/api";
import { razorpayKeyId } from "../../constants/Constants";
import {
  DEFAULT_VOICE_CREDIT_PLANS,
  VOICE_CREDIT_BASIC_PLAN,
} from "../../constants/voiceCreditPlans";
import { useVoiceCreditPlans } from "../../hooks/useVoiceCreditPlans";
import { trackGoogleAdsVoiceTokenPurchase } from "../../utils/googleAdsPurchaseConversion";
import { formatPlanCredits, formatPlanInr } from "../../utils/aiCreditPlans";
import { CheckIcon } from "@chakra-ui/icons";

export { VOICE_CREDIT_BASIC_PLAN, VOICE_CREDIT_PLANS } from "../../constants/voiceCreditPlans";

function loadRazorpayScript() {
  if (typeof window === "undefined") return Promise.resolve(false);
  if (window.Razorpay) return Promise.resolve(true);

  return new Promise((resolve) => {
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

/**
 * @param {'exhausted' | 'topUp'} [variant] — `exhausted`: out-of-credits flow. `topUp`: optional purchase from Profile.
 */
const VoiceCreditPaymentModal = ({
  isOpen,
  onClose,
  variant = "exhausted",
  initialPlanName,
}) => {
  const toast = useToast();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user || {});
  const [isPaying, setIsPaying] = useState(false);
  const [selectedPlanName, setSelectedPlanName] = useState(
    initialPlanName ||
      VOICE_CREDIT_BASIC_PLAN?.name ||
      DEFAULT_VOICE_CREDIT_PLANS[0]?.name
  );

  const { plans: voicePlans, loading: plansLoading } = useVoiceCreditPlans(isOpen);

  const headerGradient = useColorModeValue(
    "linear(to-br, teal.500, teal.600)",
    "linear(to-br, teal.600, teal.800)"
  );
  const footerBg = useColorModeValue("gray.50", "gray.900");
  const planGlow = useColorModeValue(
    "0 16px 40px rgba(45,212,171,0.2)",
    "0 16px 40px rgba(13,148,136,0.35)"
  );
  const modalBorder = useColorModeValue("teal.100", "teal.800");
  const bodyMutedTextColor = useColorModeValue("gray.600", "gray.400");
  const planCardBorderColor = useColorModeValue("teal.300", "teal.500");
  const planCardBg = useColorModeValue(
    "linear(to-br, white, teal.50)",
    "linear(to-br, gray.800, teal.950)"
  );
  const planCheckoutLabelColor = useColorModeValue("teal.600", "teal.300");
  const planTokensTextColor = useColorModeValue("gray.700", "gray.200");
  const footerBorderColor = useColorModeValue("gray.100", "gray.700");

  const remaining = useMemo(() => {
    const details = user?.userTokenDetails;
    if (!details) return 0;
    return Number(details.tokenLimit || 0) - Number(details.totalTokensUsed || 0);
  }, [user]);

  const selectedPlan = useMemo(
    () =>
      voicePlans.find((plan) => plan.name === selectedPlanName) ||
      voicePlans.find((plan) => plan.name === "Basic Plan") ||
      voicePlans[0],
    [voicePlans, selectedPlanName]
  );

  const canPay =
    Boolean(selectedPlan?.name) &&
    Number.isFinite(Number(selectedPlan?.amountInRupees)) &&
    Number(selectedPlan?.amountInRupees) > 0 &&
    Number.isFinite(Number(selectedPlan?.tokenCredits)) &&
    Number(selectedPlan?.tokenCredits) > 0;

  useEffect(() => {
    if (!voicePlans.length) return;
    if (!voicePlans.some((p) => p.name === selectedPlanName)) {
      setSelectedPlanName(
        voicePlans.find((p) => p.name === "Basic Plan")?.name || voicePlans[0].name
      );
    }
  }, [voicePlans, selectedPlanName]);

  useEffect(() => {
    if (!isOpen) return;
    if (initialPlanName) {
      setSelectedPlanName(initialPlanName);
    }
  }, [initialPlanName, isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    let isMounted = true;
    (async () => {
      try {
        const response = await hitBeApi("user", "GET");
        if (!isMounted) return;
        const userDetails = response?.data?.userDetails;
        if (userDetails) {
          dispatch({ type: "SET_USER", value: userDetails });
        }
      } catch {
        // non-blocking
      }
    })();
    return () => {
      isMounted = false;
    };
  }, [dispatch, isOpen]);

  const handlePayNow = async () => {
    if (isPaying) return;
    setIsPaying(true);
    try {
      const loaded = await loadRazorpayScript();
      if (!loaded || !window.Razorpay) {
        throw new Error("Could not load payment SDK. Please retry.");
      }

      const orderResponse = await hitBeApi("orders/razorpay", "POST", {
        plan: selectedPlan.name,
      });
      if (!orderResponse?.success) {
        throw new Error(
          orderResponse?.message || "Could not create payment order."
        );
      }
      const orderData = orderResponse?.data;
      const rzpOrderId =
        orderData?.razorpay?.orderId || orderData?.orderDetails?.orderId;
      const amount = orderData?.orderDetails?.amount;

      if (!rzpOrderId || !amount) {
        throw new Error("Invalid order response from server.");
      }

      const expectedPaise = Math.round(Number(selectedPlan.amountInRupees) * 100);
      const amountNum = Number(amount);
      if (
        !Number.isFinite(amountNum) ||
        amountNum <= 0 ||
        amountNum !== expectedPaise
      ) {
        throw new Error(
          "Checkout amount does not match the selected plan. Refresh the page and try again, or contact support."
        );
      }

      const options = {
        key: razorpayKeyId,
        amount,
        currency: orderData?.orderDetails?.currency || "INR",
        name: "Manage360.me",
        description: `AI Usage Credits top-up (${selectedPlan.tokenCredits.toLocaleString()})`,
        order_id: rzpOrderId,
        prefill: {
          name: user?.name || "",
          email: user?.email || "",
          contact: user?.phoneNumber || "",
        },
        modal: {
          ondismiss: () => {
            setIsPaying(false);
          },
        },
        handler: async (response) => {
          try {
            const pid = response?.razorpay_payment_id;
            const sig = response?.razorpay_signature;
            if (!pid || !sig) {
              throw new Error(
                "Missing Razorpay payment id or signature. Please try again."
              );
            }
            const verifyRes = await hitBeApi(
              "payments/razorpay/verify",
              "POST",
              {
                orderId: rzpOrderId,
                paymentId: pid,
                signature: sig,
                paymentPlan: selectedPlan.name,
              }
            );
            if (!verifyRes?.success) {
              throw new Error(
                verifyRes?.message || "Server did not confirm this payment."
              );
            }

            trackGoogleAdsVoiceTokenPurchase(pid);

            const freshUser = await hitBeApi("user", "GET");
            const userDetails = freshUser?.data?.userDetails;
            if (userDetails) {
              dispatch({ type: "SET_USER", value: userDetails });
            }

            toast({
              title: "Payment successful",
              description: `Added ${selectedPlan.tokenCredits.toLocaleString()} AI Usage Credits.`,
              status: "success",
              duration: 4500,
              isClosable: true,
            });
            setIsPaying(false);
            onClose();
          } catch (error) {
            toast({
              title: "Payment verification failed",
              description: error?.message || "Please contact support.",
              status: "error",
              duration: 5000,
              isClosable: true,
            });
            setIsPaying(false);
          }
        },
        theme: {
          color: "#14b8a6",
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (error) {
      toast({
        title: "Payment failed",
        description: error?.message || "Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      setIsPaying(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      isCentered
      motionPreset="scale"
      scrollBehavior="inside"
      closeOnOverlayClick={!isPaying}
    >
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(8px)" />
      <ModalContent
        mx={4}
        borderRadius="2xl"
        overflow="hidden"
        borderWidth="1px"
        borderColor={modalBorder}
        boxShadow="0 24px 64px rgba(17,94,89,0.28)"
      >
        <Flex
          direction="column"
          bgGradient={headerGradient}
          color="white"
          pt={12}
          pb={10}
          px={{ base: 6, md: 10 }}
          position="relative"
        >
          <ModalCloseButton
            isDisabled={isPaying}
            color="white"
            rounded="full"
            _hover={{ bg: "whiteAlpha.200" }}
            top={5}
          />
          <Heading
            fontSize={{ base: "xl", md: "2xl" }}
            letterSpacing="tight"
            pr={14}
          >
            {variant === "topUp" ? "Buy AI Usage Credits" : "AI Usage Credits exhausted"}
          </Heading>
          <Text mt={3} opacity={0.93} fontSize="sm" lineHeight="tall" maxW="md">
            {variant === "topUp"
              ? "Add more AI Usage Credits securely with Razorpay (INR). Your balance updates immediately after verification."
              : "Purchase an AI Usage Credits pack to use Screen AI, Media AI, and other AI features again."}
          </Text>
        </Flex>

        <ModalBody py={8} px={{ base: 6, md: 10 }}>
          <VStack align="stretch" spacing={6}>
            <Alert
              status={variant === "topUp" ? "info" : "warning"}
              borderRadius="xl"
              py={4}
              borderWidth={0}
              bg={variant === "topUp" ? "teal.50" : undefined}
              _dark={{
                bg: variant === "topUp" ? "whiteAlpha.100" : undefined,
              }}
            >
              <AlertIcon />
              <Box flex="1">
                {variant === "topUp" ? (
                  <Text fontSize="sm" lineHeight="tall">
                    You have{" "}
                    <Text as="span" fontWeight="semibold" color="teal.600">
                      {Math.max(0, remaining).toLocaleString()}
                    </Text>{" "}
                    AI Usage Credits left. Purchasing adds{" "}
                    <Text as="span" fontWeight="semibold" color="teal.700">
                      {selectedPlan.tokenCredits.toLocaleString()}
                    </Text>{" "}
                    more on your selected plan.
                  </Text>
                ) : (
                  <Text fontSize="sm">
                    Your remaining AI Usage Credits are {Math.max(0, remaining).toLocaleString()}.
                  </Text>
                )}
              </Box>
            </Alert>

            <Text fontSize="sm" color={bodyMutedTextColor}>
              {variant === "topUp"
                ? "You will confirm email and preferred payment methods in the Razorpay window."
                : `Complete checkout to instantly add ${formatPlanCredits(selectedPlan.tokenCredits)} AI Usage Credits.`}
            </Text>

            <VStack align="stretch" spacing={3}>
              {voicePlans.map((plan) => {
                const isSelected = plan.name === selectedPlan.name;
                return (
                  <Box
                    key={plan.name}
                    onClick={() => setSelectedPlanName(plan.name)}
                    borderRadius="xl"
                    py={3}
                    px={4}
                    borderWidth="2px"
                    borderColor={isSelected ? "teal.400" : planCardBorderColor}
                    bg={isSelected ? planCardBg : "transparent"}
                    boxShadow={isSelected ? planGlow : "none"}
                    cursor="pointer"
                    transition="all 0.18s ease"
                    _hover={{ transform: "translateY(-1px)", borderColor: "teal.400" }}
                    role="button"
                  >
                    <HStack justify="space-between" align="center">
                      <Box>
                        <Text
                          fontSize="xs"
                          fontWeight="bold"
                          textTransform="uppercase"
                          letterSpacing="wider"
                          color={planCheckoutLabelColor}
                        >
                          {plan.name}
                        </Text>
                        <Text fontSize="sm" color={planTokensTextColor} mt={1}>
                          +{formatPlanCredits(plan.tokenCredits)} credits
                        </Text>
                      </Box>
                      <Box textAlign="right" flexShrink={0}>
                        <Heading
                          fontSize={{ base: "xl", md: "2xl" }}
                          fontWeight="extrabold"
                          color="teal.500"
                          lineHeight="1.1"
                        >
                          {formatPlanInr(plan.amountInRupees)}
                        </Heading>
                        {isSelected ? (
                          <Badge
                            mt={1}
                            colorScheme="teal"
                            variant="subtle"
                            borderRadius="full"
                          >
                            <HStack spacing={1}>
                              <Icon as={CheckIcon} boxSize={3} />
                              <Text fontSize="0.65rem">Selected</Text>
                            </HStack>
                          </Badge>
                        ) : null}
                      </Box>
                    </HStack>
                  </Box>
                );
              })}
            </VStack>
          </VStack>
        </ModalBody>
        <ModalFooter
          bg={footerBg}
          borderTopWidth="1px"
          borderColor={footerBorderColor}
          py={6}
          px={{ base: 6, md: 10 }}
          gap={3}
          flexWrap="wrap"
        >
          <Button mr="auto" variant="ghost" onClick={onClose} isDisabled={isPaying} size="lg">
            Cancel
          </Button>
          <Button
            ml="auto"
            size="lg"
            px={{ base: 8, md: 10 }}
            minW={{ base: "100%", sm: "200px" }}
            onClick={handlePayNow}
            isDisabled={!canPay}
            isLoading={isPaying || (plansLoading && !canPay)}
            loadingText={plansLoading ? "Loading plans" : "Opening checkout"}
            rounded="xl"
            color="white"
            bgGradient="linear(to-r, teal.400, teal.600)"
            boxShadow="0 10px 32px rgba(20,184,166,0.45)"
            _hover={{
              bgGradient: "linear(to-r, teal.500, teal.700)",
              transform: "translateY(-2px)",
            }}
            _active={{ transform: "translateY(0)" }}
            transition="all 0.2s ease"
          >
            Pay {formatPlanInr(selectedPlan.amountInRupees)}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default VoiceCreditPaymentModal;
